import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useOrganizations } from "@/hooks/useOrganizations";
import { usePermissions } from "@/hooks/usePermissions";
import { useRoles } from "@/hooks/useRoles";
import { useCreateUser } from "@/hooks/useUsers";
import type { Role } from "@/lib/roles";
import { ROLES } from "@/lib/roles";

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * When set, the Organization field is locked to this value (read-only) and its id
   * is always sent as `organizationId`. Used by Account → Users so a Client Admin
   * cannot re-target a different org. Omit to keep the existing GFG autocomplete behavior.
   */
  fixedOrg?: { id: number; name: string };
}

type OrgOption = { id: number; name: string };

/**
 * Invite (create) a user. Sends a Clerk invitation via POST /admin/users. GFG staff
 * pick the target Organization (the new user lands in its root group); a client admin's
 * org is set server-side from their own. Only org-level roles are offered here — GFG
 * staff roles are assigned afterward from the Edit User dialog.
 *
 * When `fixedOrg` is provided the Organization field is shown as read-only and its id
 * is always included in the payload — used by Account → Users to prevent a Client Admin
 * from retargeting a different org. The backend enforces the same restriction via
 * scopeWriteGuard, so this is a UX complement to an existing security control.
 */
export default function InviteUserDialog({ open, onClose, fixedOrg }: InviteUserDialogProps) {
  const { isGfg } = usePermissions();
  const orgLocked = fixedOrg !== undefined;
  const { roles } = useRoles();
  const createUser = useCreateUser();

  const orgRoles = roles.filter((r) => r.level === "org");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>(ROLES.CLIENT_ADMIN);
  const [org, setOrg] = useState<OrgOption | null>(null);
  const [orgInput, setOrgInput] = useState("");
  const [orgSearch, setOrgSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Reset when reopened.
  useEffect(() => {
    if (open) {
      setFirstName("");
      setLastName("");
      setEmail("");
      setRole(ROLES.CLIENT_ADMIN);
      setOrg(null);
      setOrgInput("");
      setOrgSearch("");
      setError(null);
    }
  }, [open]);

  // Debounce the org typeahead.
  useEffect(() => {
    const t = setTimeout(() => setOrgSearch(orgInput), 300);
    return () => clearTimeout(t);
  }, [orgInput]);

  // Skip the org typeahead entirely when the org is fixed — no need to fetch all orgs.
  const { data: orgData, isFetching: orgsLoading } = useOrganizations({
    search: orgSearch || undefined,
    limit: 20,
    enabled: isGfg && !orgLocked,
  });
  const orgOptions: OrgOption[] = (orgData?.data ?? []).map((o) => ({ id: o.id, name: o.name }));

  const emailValid = /.+@.+\..+/.test(email.trim());
  const canSubmit =
    emailValid &&
    firstName.trim().length > 0 &&
    // Org is satisfied if: it's locked (fixedOrg), or the caller isn't GFG, or they've picked one.
    (!isGfg || orgLocked || org != null) &&
    !createUser.isPending;

  function handleSubmit() {
    setError(null);
    createUser.mutate(
      {
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        role,
        // fixedOrg takes precedence; falls back to the GFG-picked org; omit for client admins
        // (backend then uses requester.organizationId from the auth context).
        organizationId: fixedOrg?.id ?? (isGfg ? (org?.id ?? undefined) : undefined),
      },
      {
        onSuccess: () => onClose(),
        onError: (e) => setError(e instanceof Error ? e.message : "Failed to invite user."),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={createUser.isPending ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontFamily: "var(--font-crimson)", fontSize: 24 }}>
        Invite user
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              size="small"
            />
          </Box>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
          />

          {orgLocked ? (
            // Org is pre-determined (e.g. Account → Users) — show it as read-only so the
            // user understands which org they're inviting into, but cannot change it.
            <TextField
              label="Organization"
              value={fixedOrg!.name}
              disabled
              fullWidth
              size="small"
            />
          ) : (
            isGfg && (
              <Autocomplete<OrgOption>
                options={orgOptions}
                loading={orgsLoading}
                value={org}
                onChange={(_e, v) => setOrg(v)}
                inputValue={orgInput}
                onInputChange={(_e, v) => setOrgInput(v)}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField {...params} label="Organization" size="small" />
                )}
              />
            )
          )}

          <TextField
            select
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            fullWidth
            size="small"
          >
            {orgRoles.map((r) => (
              <MenuItem key={r.key} value={r.key}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          {error && <Typography sx={{ fontSize: 13, color: "error.main" }}>{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={createUser.isPending}
          sx={{ color: "var(--color-graphite)" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          sx={{ bgcolor: "var(--color-rodeo-black)" }}
        >
          {createUser.isPending ? "Inviting…" : "Send invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
